import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Play, Pause, RotateCw } from 'lucide-react';

interface BoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
  confidence: number;
  frameTimeMs: number;
}

interface Appearance {
  startTimeSeconds: number;
  endTimeSeconds: number;
  durationSeconds: number;
  confidence: number;
  frameCount: number;
  boundingBoxes: BoundingBox[];
}

interface BrandVideoPlayerProps {
  videoSrc: string;
  appearances: Appearance[];
  brandName: string;
  onError?: () => void;
}

const BrandVideoPlayer: React.FC<BrandVideoPlayerProps> = ({
  videoSrc,
  appearances,
  brandName: _brandName,
  onError
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showBoundingBoxes, setShowBoundingBoxes] = useState(true);
  const [activeBoundingBox, setActiveBoundingBox] = useState<BoundingBox | null>(null);


  // Find the current bounding box based on video time
  useEffect(() => {
    if (!showBoundingBoxes || !appearances.length) {
      setActiveBoundingBox(null);
      return;
    }

    // Convert current time to milliseconds
    const currentTimeMs = currentTime * 1000;

    // Find the active appearance
    const activeAppearance = appearances.find(
      app => currentTime >= app.startTimeSeconds && currentTime <= app.endTimeSeconds
    );

    if (activeAppearance) {
      // Find the closest bounding box by time
      let closestBox: BoundingBox | null = null;
      let minTimeDiff = Infinity;

      activeAppearance.boundingBoxes.forEach(box => {
        const timeDiff = Math.abs(box.frameTimeMs - currentTimeMs);
        if (timeDiff < minTimeDiff) {
          minTimeDiff = timeDiff;
          closestBox = box;
        }
      });

      // Only show box if we're within a reasonable time range (e.g., 500ms)
      if (closestBox && minTimeDiff < 500) {
        setActiveBoundingBox(closestBox);
      } else {
        setActiveBoundingBox(null);
      }
    } else {
      setActiveBoundingBox(null);
    }
  }, [currentTime, appearances, showBoundingBoxes]);

  // Handle time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // Play/Pause toggle
  const togglePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Restart video
  const restartVideo = () => {
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  return (
    <div className="relative bg-black rounded-lg overflow-hidden">
      {/* Video container with aspect ratio */}
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <div className="absolute inset-0">
          {/* Video element with overlay wrapper */}
          <div className="relative w-full h-full flex items-center justify-center">
            <video
              ref={videoRef}
              src={videoSrc}
              className="absolute inset-0 w-full h-full object-contain"
              onTimeUpdate={handleTimeUpdate}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              onError={onError}
              controls={false}
            />

            {/* Bounding Box Overlay */}
            <AnimatePresence>
              {activeBoundingBox && showBoundingBoxes && videoRef.current && (
                <motion.div
                  key={`box-${activeBoundingBox.frameTimeMs}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.1 }}
                  className="absolute inset-0 pointer-events-none"
                >
                  {(() => {
                    const video = videoRef.current;
                    const containerRect = video.parentElement?.getBoundingClientRect();
                    
                    if (!containerRect) return null;

                    // Calculate the actual displayed video dimensions (accounting for object-contain)
                    const videoAspectRatio = video.videoWidth / video.videoHeight;
                    const containerAspectRatio = containerRect.width / containerRect.height;
                    
                    let displayWidth, displayHeight, offsetX = 0, offsetY = 0;
                    
                    if (videoAspectRatio > containerAspectRatio) {
                      // Video is wider - fit to width
                      displayWidth = containerRect.width;
                      displayHeight = containerRect.width / videoAspectRatio;
                      offsetY = (containerRect.height - displayHeight) / 2;
                    } else {
                      // Video is taller - fit to height
                      displayHeight = containerRect.height;
                      displayWidth = containerRect.height * videoAspectRatio;
                      offsetX = (containerRect.width - displayWidth) / 2;
                    }

                    // Bounding box coordinates are normalized (0.0 to 1.0), so scale directly to display size
                    // If Y coordinates need inversion, use activeBoundingBox.y directly (top-left origin)
                    const boxStyle = {
                      left: `${offsetX + (activeBoundingBox.x * displayWidth)}px`,
                      top: `${offsetY + (activeBoundingBox.y * displayHeight)}px`,
                      width: `${activeBoundingBox.width * displayWidth}px`,
                      height: `${activeBoundingBox.height * displayHeight}px`
                    };



                    return (
                      <div
                        className="absolute border-4 border-green-500 bg-green-500 bg-opacity-10 rounded-lg shadow-lg"
                        style={boxStyle}
                      >
                        {/* Corner indicators for precision */}
                        <div className="absolute -top-2 -left-2 w-6 h-6 border-t-4 border-l-4 border-green-500 rounded-tl-lg" />
                        <div className="absolute -top-2 -right-2 w-6 h-6 border-t-4 border-r-4 border-green-500 rounded-tr-lg" />
                        <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b-4 border-l-4 border-green-500 rounded-bl-lg" />
                        <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b-4 border-r-4 border-green-500 rounded-br-lg" />
                      </div>
                    );
                  })()}
                </motion.div>
              )}
            </AnimatePresence>
          </div>


          {/* Video Controls Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={togglePlayPause}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  {isPlaying ? (
                    <Pause className="h-5 w-5 text-white" />
                  ) : (
                    <Play className="h-5 w-5 text-white" />
                  )}
                </button>
                <button
                  onClick={restartVideo}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                >
                  <RotateCw className="h-4 w-4 text-white" />
                </button>
              </div>

              <button
                onClick={() => setShowBoundingBoxes(!showBoundingBoxes)}
                className={`flex items-center space-x-2 px-3 py-1.5 rounded-full transition-colors ${
                  showBoundingBoxes ? 'bg-green-500 text-white' : 'bg-white/20 text-white'
                }`}
              >
                {showBoundingBoxes ? (
                  <>
                    <Eye className="h-4 w-4" />
                    <span className="text-sm">Detections On</span>
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4" />
                    <span className="text-sm">Detections Off</span>
                  </>
                )}
              </button>
            </div>

            {/* Timeline with appearance indicators */}
            <div className="mt-3 relative">
              <input
                type="range"
                min="0"
                max={videoRef.current?.duration || 0}
                value={currentTime}
                onChange={(e) => {
                  const newTime = parseFloat(e.target.value);
                  if (videoRef.current) {
                    videoRef.current.currentTime = newTime;
                    setCurrentTime(newTime);
                  }
                }}
                className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #10b981 0%, #10b981 ${(currentTime / (videoRef.current?.duration || 1)) * 100}%, rgba(255,255,255,0.2) ${(currentTime / (videoRef.current?.duration || 1)) * 100}%, rgba(255,255,255,0.2) 100%)`
                }}
              />
              
              {/* Appearance indicators on timeline */}
              {appearances.map((app, index) => {
                const startPercent = (app.startTimeSeconds / (videoRef.current?.duration || 1)) * 100;
                const widthPercent = (app.durationSeconds / (videoRef.current?.duration || 1)) * 100;
                
                return (
                  <div
                    key={index}
                    className="absolute top-0 h-2 bg-green-400/50 pointer-events-none"
                    style={{
                      left: `${startPercent}%`,
                      width: `${widthPercent}%`
                    }}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandVideoPlayer;